import type z from 'zod/v4'
import { relations } from 'drizzle-orm'
import { index, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsHighPrecision } from '../../custom-type'
import { organization } from '../auth'
import { Royalty } from './royalty'
import { RoyaltyWithdrawal } from './royalty-withdrawal'

export const RoyaltyTransaction = pgTable(
  'royalty_transaction',
  {
    id: uuid().primaryKey().defaultRandom(),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'restrict' }),
    transactionType: text({ enum: ['ROYALTY', 'WITHDRAWAL', 'INITIAL_BALANCE'] }).notNull(),
    royaltyId: uuid().references(() => Royalty.id, { onDelete: 'restrict' }),
    withdrawalId: uuid().references(() => RoyaltyWithdrawal.id, { onDelete: 'restrict' }),
    amount: numericAsHighPrecision().notNull(),
    currency: text().notNull().default('IDR'),
    description: text(),
    runningBalance: numericAsHighPrecision().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('royalty_transaction_organization_idx').on(t.organizationId),
    index('royalty_transaction_type_idx').on(t.transactionType),
    index('royalty_transaction_created_at_idx').on(t.createdAt),
    index('royalty_transaction_organization_created_at_idx').on(t.organizationId, t.createdAt),
  ],
)

export const royaltyTransactionRelations = relations(RoyaltyTransaction, ({ one }) => ({
  organization: one(organization, {
    fields: [RoyaltyTransaction.organizationId],
    references: [organization.id],
  }),
  royalty: one(Royalty, {
    fields: [RoyaltyTransaction.royaltyId],
    references: [Royalty.id],
  }),
  withdrawal: one(RoyaltyWithdrawal, {
    fields: [RoyaltyTransaction.withdrawalId],
    references: [RoyaltyWithdrawal.id],
  }),
}))

export const InsertRoyaltyTransactionSchema = createInsertSchema(RoyaltyTransaction)
  .omit({
    id: true,
    createdAt: true,
  })
  .superRefine((data, ctx) => {
    if (data.transactionType === 'ROYALTY' && !data.royaltyId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Royalty ID is required',
        path: ['royaltyId'],
      })
    }

    if (data.transactionType === 'WITHDRAWAL' && !data.withdrawalId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Withdrawal ID is required',
        path: ['withdrawalId'],
      })
    }
  })

export const UpdateRoyaltyTransactionSchema = createUpdateSchema(RoyaltyTransaction)
  .partial()
  .omit({
    id: true,
    organizationId: true,
    createdAt: true,
  })
  .superRefine((data, ctx) => {
    if (data.transactionType === 'ROYALTY' && !data.royaltyId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Royalty ID is required',
        path: ['royaltyId'],
      })
    }

    if (data.transactionType === 'WITHDRAWAL' && !data.withdrawalId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Withdrawal ID is required',
        path: ['withdrawalId'],
      })
    }
  })

export const SelectRoyaltyTransactionSchema = createSelectSchema(RoyaltyTransaction)

export type InsertRoyaltyTransaction = typeof RoyaltyTransaction.$inferInsert
export type UpdateRoyaltyTransaction = z.infer<typeof UpdateRoyaltyTransactionSchema>
export type SelectRoyaltyTransaction = typeof RoyaltyTransaction.$inferSelect
